import { useState, useEffect, useCallback } from 'react';
import type {
  Appointment,
  ReceptionStats,
  VisitStatus, 
  AppointmentsQueryParams 
} from '@app-types/reception';
import { receptionService } from '@services/receptionService';

export const useReceptionData = (initialDate: string, _clinicId?: string) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [stats, setStats] = useState<ReceptionStats | null>(null);
  const [delayAlerts, setDelayAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(initialDate);

  // Parametri de filtrare
  const [filters, setFilters] = useState<{
    status?: VisitStatus[];
    doctorId?: string;
    search?: string;
  }>({});

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0
  });

  /**
   * Incarca toate datele pentru dashboard
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: AppointmentsQueryParams = {
        date: currentDate,
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...filters,
        q: filters.search
      };

      // Incarca in paralel: programari, statistici, alerte
      const [appointmentsRes, statsRes, alertsRes] = await Promise.all([
        receptionService.getAppointments(params),
        receptionService.getStats(currentDate),
        receptionService.getDelayAlerts(currentDate, 15)
      ]);

      setAppointments(appointmentsRes.items);
      setPagination(prev => ({
        ...prev,
        total: appointmentsRes.total
      }));
      setStats(statsRes);
      setDelayAlerts(alertsRes);
    } catch (err: any) {
      console.error('Eroare incarcare date receptie:', err);
      setError(err.response?.data?.error || 'Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [currentDate, pagination.page, pagination.pageSize, filters]);

  /**
   * Reincarca datele
   */
  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);

  /**
   * Check-in pentru o programare
   */
  const checkin = useCallback(async (appointmentId: string) => {
    try {
      const updated = await receptionService.checkin(appointmentId);
      setAppointments(prev => 
        prev.map(appt => appt.id === appointmentId ? updated : appt)
      );
      // Reincarca statisticile
      const statsRes = await receptionService.getStats(currentDate);
      setStats(statsRes);
      return updated;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Eroare la check-in');
    }
  }, [currentDate]);

  /**
   * Schimba statusul unei programari
   */
  const changeStatus = useCallback(async (
    appointmentId: string, 
    status: Exclude<VisitStatus, "scheduled">
  ) => {
    try {
      const updated = await receptionService.setStatus(appointmentId, status);
      setAppointments(prev => 
        prev.map(appt => appt.id === appointmentId ? updated : appt)
      );
      // Reincarca statisticile
      const statsRes = await receptionService.getStats(currentDate);
      setStats(statsRes);
      return updated;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Eroare la schimbarea statusului');
    }
  }, [currentDate]);

  /**
   * Anuleaza o programare
   */
  const cancelAppointment = useCallback(async (appointmentId: string, reason?: string) => {
    try {
      const updated = await receptionService.cancel(appointmentId, reason);
      setAppointments(prev => 
        prev.map(appt => appt.id === appointmentId ? updated : appt)
      );
      // Reincarca statisticile
      const statsRes = await receptionService.getStats(currentDate);
      setStats(statsRes);
      return updated;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Eroare la anulare');
    }
  }, [currentDate]);

  /**
   * Creeaza o programare noua
   */
  const createAppointment = useCallback(async (data: {
    clinicId: string;
    animalId: string;
    ownerId: string;
    doctorId: string;
    serviceType: string;
    scheduledAt: string;
    notes?: string;
  }) => {
    try {
      const created = await receptionService.createAppointment(data);
      refresh(); // Reincarca toate datele
      return created;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Eroare la crearea programarii');
    }
  }, [refresh]);

  /**
   * Schimba data curenta
   */
  const changeDate = useCallback((newDate: string) => {
    setCurrentDate(newDate);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  /**
   * Actualizeaza filtrele
   */
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  /**
   * Schimba pagina
   */
  const changePage = useCallback((newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  /**
   * Schimba marimea paginii
   */
  const changePageSize = useCallback((newPageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
  }, []);

  // Incarca datele la montare si cand se schimba parametrii
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Date
    appointments,
    stats,
    delayAlerts,
    loading,
    error,
    currentDate,
    filters,
    pagination,

    // Actiuni
    refresh,
    checkin,
    changeStatus,
    cancelAppointment,
    createAppointment,
    changeDate,
    updateFilters,
    changePage,
    changePageSize
  };
};

